import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import ShieldIcon from "@mui/icons-material/Shield";
import { theme } from "../../theme/colors";
import { normalizeAndValidateMfaPhone } from "../../utils/validateMfaPhone";
import {
  getAuth,
  multiFactor,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
} from "firebase/auth";
import {
  getProviderType,
  reauthenticateUser,
  startMfaReauthChallenge,
  completeMfaReauth,
} from "../../hooks/useReauthentication";

export default function SecuritySection({ userPhoneNumber }) {
  const authInstance = getAuth();
  const user = authInstance.currentUser;

  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [smsSent, setSmsSent] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [verificationId, setVerificationId] = useState(null);
  const [verifyCode, setVerifyCode] = useState("");
  const [phoneFieldError, setPhoneFieldError] = useState("");
  const [smsDestinationE164, setSmsDestinationE164] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const [reAuthOpen, setReAuthOpen] = useState(false);
  const [reAuthPassword, setReAuthPassword] = useState("");
  const [reAuthError, setReAuthError] = useState("");
  const [reAuthLoading, setReAuthLoading] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  const [reAuthMfaResolver, setReAuthMfaResolver] = useState(null);
  const [reAuthMfaVerificationId, setReAuthMfaVerificationId] = useState(null);
  const [reAuthMfaCode, setReAuthMfaCode] = useState("");
  const [reAuthMfaSmsSending, setReAuthMfaSmsSending] = useState(false);

  const recaptchaContainerRef = useRef(null);
  const recaptchaVerifierRef = useRef(null);

  useEffect(() => {
    if (user) {
      const enrolled = multiFactor(user).enrolledFactors;
      setMfaEnabled(
        enrolled.some((f) => f.factorId === PhoneMultiFactorGenerator.FACTOR_ID)
      );
    }
  }, [user]);

  useEffect(() => {
    if (userPhoneNumber) setPhoneNumber(userPhoneNumber);
  }, [userPhoneNumber]);

  useEffect(() => {
    return () => {
      if (recaptchaVerifierRef.current) {
        recaptchaVerifierRef.current.clear();
        recaptchaVerifierRef.current = null;
      }
    };
  }, []);

  const getRecaptchaVerifier = () => {
    if (recaptchaVerifierRef.current) {
      recaptchaVerifierRef.current.clear();
      recaptchaVerifierRef.current = null;
    }
    if (recaptchaContainerRef.current) {
      recaptchaContainerRef.current.innerHTML = "";
    }
    recaptchaVerifierRef.current = new RecaptchaVerifier(authInstance, recaptchaContainerRef.current, {
      size: "invisible",
    });
    return recaptchaVerifierRef.current;
  };

  const doReAuth = async () => {
    await reauthenticateUser(user, { authInstance });
  };

  const requireReAuth = (action) => {
    setError("");
    setSuccess("");
    setPhoneFieldError("");
    const providerType = getProviderType(user);
    setPendingAction(action);

    if (providerType === "password") {
      setReAuthOpen(true);
      setReAuthPassword("");
      setReAuthError("");
    } else {
      handleReAuthConfirm(action, providerType);
    }
  };

  const handleReAuthMfaChallenge = async (err) => {
    setReAuthMfaSmsSending(true);
    try {
      const { resolver, verificationId } = await startMfaReauthChallenge(err, {
        authInstance,
        recaptchaContainerRef,
        recaptchaVerifierRef,
      });
      setReAuthMfaResolver(resolver);
      setReAuthMfaVerificationId(verificationId);
    } finally {
      setReAuthMfaSmsSending(false);
    }
  };

  const handleReAuthMfaVerify = async () => {
    if (!reAuthMfaResolver || !reAuthMfaVerificationId || reAuthMfaCode.length !== 6) return;
    setReAuthLoading(true);
    setReAuthError("");
    try {
      await completeMfaReauth(reAuthMfaResolver, reAuthMfaVerificationId, reAuthMfaCode);
      setReAuthOpen(false);
      setReAuthMfaResolver(null);
      setReAuthMfaVerificationId(null);
      setReAuthMfaCode("");
      const action = pendingAction;
      if (action === "enroll") {
        setEnrolling(true);
      } else if (action === "unenroll") {
        await doUnenroll();
      }
    } catch (err) {
      if (err.code === "auth/invalid-verification-code") {
        setReAuthError("Invalid code. Check your SMS and try again.");
      } else {
        setReAuthError(err.message || "Verification failed.");
      }
    } finally {
      setReAuthLoading(false);
    }
  };

  const handleReAuthConfirm = async (actionOverride, providerOverride) => {
    const action = actionOverride || pendingAction;
    setReAuthLoading(true);
    setReAuthError("");
    try {
      const providerType = providerOverride || getProviderType(user);
      if (providerType === "password") {
        await reauthenticateUser(user, { password: reAuthPassword, authInstance });
      } else {
        await doReAuth();
      }
      setReAuthOpen(false);
      if (action === "enroll") {
        setEnrolling(true);
      } else if (action === "unenroll") {
        await doUnenroll();
      }
    } catch (err) {
      if (err.code === "auth/multi-factor-auth-required") {
        try {
          await handleReAuthMfaChallenge(err);
        } catch (mfaErr) {
          setReAuthError(mfaErr.message || "Failed to send verification code.");
        } finally {
          setReAuthLoading(false);
        }
        return;
      }
      const msg =
        err.code === "auth/wrong-password" || err.code === "auth/invalid-credential"
          ? "Incorrect password. Please try again."
          : err.code === "auth/popup-closed-by-user"
            ? "Sign-in popup was closed. Please try again."
            : err.message || "Re-authentication failed.";

      if (reAuthOpen) {
        setReAuthError(msg);
      } else {
        setError(msg);
      }
    } finally {
      setReAuthLoading(false);
    }
  };

  const handleSendCode = async () => {
    setLoading(true);
    setError("");
    setPhoneFieldError("");
    const { e164, error: phoneErr } = normalizeAndValidateMfaPhone(phoneNumber);
    if (phoneErr) {
      setPhoneFieldError(phoneErr);
      setError(phoneErr);
      setLoading(false);
      return;
    }
    try {
      const session = await multiFactor(user).getSession();
      const recaptchaVerifier = getRecaptchaVerifier();
      const phoneInfoOptions = {
        phoneNumber: e164,
        session,
      };
      const phoneAuthProvider = new PhoneAuthProvider(authInstance);
      const vId = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier);
      setVerificationId(vId);
      setSmsSent(true);
    } catch (err) {
      if (err.code === "auth/invalid-phone-number") {
        setError("Invalid phone number. Please include the country code (e.g. +1).");
      } else if (err.code === "auth/too-many-requests") {
        setError("Too many requests. Please wait a few minutes and try again.");
      } else {
        setError(err.message || "Failed to send verification code.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndEnroll = async () => {
    setError("");
    setLoading(true);
    try {
      const credential = PhoneAuthProvider.credential(verificationId, verifyCode);
      const assertion = PhoneMultiFactorGenerator.assertion(credential);
      await multiFactor(user).enroll(assertion, "Phone Number");
      setMfaEnabled(true);
      setEnrolling(false);
      setSmsSent(false);
      setVerificationId(null);
      setVerifyCode("");
      setSmsDestinationE164("");
      setPhoneFieldError("");
      setSuccess("Two-factor authentication enabled!");
    } catch (err) {
      if (err.code === "auth/invalid-verification-code") {
        setError("Invalid code. Please check your SMS and try again.");
      } else {
        setError(err.message || "Failed to verify code.");
      }
    } finally {
      setLoading(false);
    }
  };

  const doUnenroll = async () => {
    setLoading(true);
    setError("");
    try {
      const enrolled = multiFactor(user).enrolledFactors;
      const phoneFactor = enrolled.find(
        (f) => f.factorId === PhoneMultiFactorGenerator.FACTOR_ID
      );
      if (phoneFactor) {
        await multiFactor(user).unenroll(phoneFactor);
        setMfaEnabled(false);
        setSuccess("Two-factor authentication disabled.");
      }
    } catch (err) {
      setError(err.message || "Failed to disable 2FA.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEnroll = () => {
    setEnrolling(false);
    setSmsSent(false);
    setVerificationId(null);
    setVerifyCode("");
    setError("");
    setPhoneFieldError("");
    setSmsDestinationE164("");
  };

  if (!user) return null;

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, color: "#111", mb: 2 }}>
        Security
      </Typography>

      <Box sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: 2, border: "1px solid #e2e8f0" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          <ShieldIcon sx={{ color: mfaEnabled ? theme.primary : "#94a3b8" }} />
          <Typography sx={{ fontWeight: 600, color: "#0f172a", fontSize: "0.95rem" }}>
            Two-Factor Authentication (SMS)
          </Typography>
          <Chip
            label={mfaEnabled ? "Enabled" : "Disabled"}
            size="small"
            sx={{
              fontWeight: 700,
              fontSize: "0.75rem",
              bgcolor: mfaEnabled ? "#ecfdf5" : "#f1f5f9",
              color: mfaEnabled ? "#0f172a" : "#64748b",
              border: `1px solid ${mfaEnabled ? "#a7f3d0" : "#e2e8f0"}`,
            }}
          />
        </Box>

        {!enrolling && !mfaEnabled && (
          <Button
            variant="contained"
            onClick={() => requireReAuth("enroll")}
            disabled={loading}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              bgcolor: theme.primary,
              color: "#ffffff",
              "&:hover": { bgcolor: theme.primaryHover },
              "&.Mui-disabled": { bgcolor: "#e2e8f0", color: "#94a3b8" },
            }}
          >
            {loading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Enable 2FA"}
          </Button>
        )}

        {!enrolling && mfaEnabled && (
          <Button
            variant="outlined"
            onClick={() => requireReAuth("unenroll")}
            disabled={loading}
            sx={{
              textTransform: "none",
              fontWeight: 500,
              color: "#475569",
              borderColor: "#cbd5e1",
              "&:hover": { bgcolor: "#fef2f2", color: "#dc2626", borderColor: "#fca5a5" },
            }}
          >
            {loading ? <CircularProgress size={18} /> : "Disable 2FA"}
          </Button>
        )}

        {enrolling && !smsSent && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ color: "#0f172a", fontWeight: 600, mb: 1, fontSize: "0.9rem" }}>
              Confirm your phone number:
            </Typography>
            <Typography variant="body2" sx={{ color: "#334155", mb: 0.5, fontSize: "0.85rem" }}>
              Include your country code (e.g. +1 for US). We will send a verification code via SMS.
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748b", mb: 1.5, fontSize: "0.8rem" }}>
              If you lose access to this phone number, a verified Recovery Email can help you regain access through support.
            </Typography>

            <TextField
              size="small"
              type="tel"
              placeholder="+1 (555) 123-4567"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              inputProps={{ style: { fontSize: 16 } }}
              sx={{ mb: 2, width: 280, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />

            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleSendCode}
                disabled={!phoneNumber.trim() || loading}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  bgcolor: theme.primary,
                  color: "#ffffff",
                  "&:hover": { bgcolor: theme.primaryHover },
                  "&.Mui-disabled": { bgcolor: "#e2e8f0", color: "#94a3b8" },
                }}
              >
                {loading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Send Verification Code"}
              </Button>
              <Button
                variant="outlined"
                onClick={handleCancelEnroll}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  color: "#334155",
                  borderColor: "#e2e8f0",
                  "&:hover": { bgcolor: "#f1f5f9", borderColor: "#cbd5e1" },
                }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        )}

        {enrolling && smsSent && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ color: "#0f172a", mb: 1.5, fontWeight: 500 }}>
              We sent a code to <strong>{smsDestinationE164}</strong>. Enter it below:
            </Typography>

            <TextField
              size="small"
              placeholder="000000"
              value={verifyCode}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, "").slice(0, 6);
                setVerifyCode(val);
              }}
              inputProps={{
                maxLength: 6,
                style: { fontSize: 24, letterSpacing: 8, textAlign: "center", fontWeight: 700 },
                inputMode: "numeric",
              }}
              sx={{ mb: 2, width: 220, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />

            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleVerifyAndEnroll}
                disabled={verifyCode.length !== 6 || loading}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  bgcolor: theme.primary,
                  color: "#ffffff",
                  "&:hover": { bgcolor: theme.primaryHover },
                  "&.Mui-disabled": { bgcolor: "#e2e8f0", color: "#94a3b8" },
                }}
              >
                {loading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Verify & Enable"}
              </Button>
              <Button
                variant="outlined"
                onClick={handleCancelEnroll}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  color: "#334155",
                  borderColor: "#e2e8f0",
                  "&:hover": { bgcolor: "#f1f5f9", borderColor: "#cbd5e1" },
                }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        )}

        {success && (
          <Alert 
            severity="success" 
            sx={{ 
              mt: 2, 
              fontWeight: 500,
              bgcolor: "#f0fdf4",
              color: "#166534",
              border: "1px solid #bbf7d0",
              "& .MuiAlert-icon": { color: "#16a34a" }
            }}
          >
            {success}
          </Alert>
        )}
        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mt: 2, 
              fontWeight: 500,
              bgcolor: "#fef2f2",
              color: "#991b1b",
              border: "1px solid #fecaca",
              "& .MuiAlert-icon": { color: "#dc2626" }
            }}
          >
            {error}
          </Alert>
        )}
      </Box>

      <div ref={recaptchaContainerRef} />

      {/* Re-auth Dialog */}
      <Dialog
        open={reAuthOpen}
        onClose={() => {
          if (!reAuthLoading) {
            setReAuthOpen(false);
            setReAuthMfaResolver(null);
            setReAuthMfaVerificationId(null);
            setReAuthMfaCode("");
          }
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: "#0f172a" }}>
          {reAuthMfaVerificationId ? "Verify Your Identity" : "Confirm Your Password"}
        </DialogTitle>
        <DialogContent>
          {reAuthMfaVerificationId ? (
            <>
              <Typography variant="body2" sx={{ color: "#334155", mb: 2 }}>
                Enter the 6-digit code sent to your phone via SMS.
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="000000"
                value={reAuthMfaCode}
                onChange={(e) => setReAuthMfaCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                onKeyDown={(e) => e.key === "Enter" && handleReAuthMfaVerify()}
                inputProps={{
                  maxLength: 6,
                  style: { fontSize: 24, letterSpacing: 8, textAlign: "center", fontWeight: 700 },
                  inputMode: "numeric",
                }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </>
          ) : reAuthMfaSmsSending ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" sx={{ color: "#334155" }}>
                Sending verification code to your phone...
              </Typography>
            </Box>
          ) : (
            <>
              <Typography variant="body2" sx={{ color: "#334155", mb: 2 }}>
                For your security, please re-enter your password to continue.
              </Typography>
              <TextField
                fullWidth
                type="password"
                size="small"
                placeholder="Your password"
                value={reAuthPassword}
                onChange={(e) => setReAuthPassword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleReAuthConfirm()}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </>
          )}
          {reAuthError && (
            <Alert severity="error" sx={{ mt: 2, color: "#dc2626" }}>
              {reAuthError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setReAuthOpen(false);
              setReAuthMfaResolver(null);
              setReAuthMfaVerificationId(null);
              setReAuthMfaCode("");
            }}
            disabled={reAuthLoading}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: "#334155",
              borderColor: "#e2e8f0",
              "&:hover": { bgcolor: "#f1f5f9", borderColor: "#cbd5e1" },
            }}
          >
            Cancel
          </Button>
          {reAuthMfaVerificationId ? (
            <Button
              variant="contained"
              onClick={handleReAuthMfaVerify}
              disabled={reAuthMfaCode.length !== 6 || reAuthLoading}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                bgcolor: theme.primary,
                color: "#ffffff",
                "&:hover": { bgcolor: theme.primaryHover },
                "&.Mui-disabled": { bgcolor: "#e2e8f0", color: "#94a3b8" },
              }}
            >
              {reAuthLoading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Verify"}
            </Button>
          ) : !reAuthMfaSmsSending ? (
            <Button
              variant="contained"
              onClick={() => handleReAuthConfirm()}
              disabled={!reAuthPassword || reAuthLoading}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                bgcolor: theme.primary,
                color: "#ffffff",
                "&:hover": { bgcolor: theme.primaryHover },
                "&.Mui-disabled": { bgcolor: "#e2e8f0", color: "#94a3b8" },
              }}
            >
              {reAuthLoading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Confirm"}
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
